import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExportOutlined,
  ReloadOutlined,
  SolutionOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { PhoneInput } from "../../../components/PhoneInput";
import { CreatableSelect } from "../../../components/CreatableSelect";
import { businessPositionService } from "../../../services/api/businessPositionService";
import { usePageTitle } from "../../../hooks";
import { formatPhone } from "../../../utils/phone";

const { Text, Paragraph } = Typography;

type Kind = "new" | "edit" | "remove";

interface ProposedFields {
  name?: string;
  phone?: string;
  email?: string;
  position?: string;
}

interface BusinessPosition {
  id: number;
  name: string;
}

interface ApplyFormValues {
  name?: string;
  phone?: string;
  email?: string;
  position_id?: number;
  notes?: string;
}

interface CurrentContact {
  producer_contact_id: string;
  is_active: boolean;
  full_name: string;
  email: string | null;
  phone: string | null;
  position_id: number | null;
  position_name: string | null;
}

interface ProposalRow {
  ticket_id: number;
  ticket_code: string;
  subject: string;
  status: number; // 1=open, 2=resolved, 3=closed
  status_label: string;
  created: string;
  created_by: string | null;
  resolved_date: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  audit_id: number | null;
  audit_reference: string | null;
  producer_id: number | null;
  producer_name: string | null;
  farm_id: number | null;
  farm_name: string | null;
  kind: Kind;
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  proposed: ProposedFields | null;
  reason: string | null;
  current_contact: CurrentContact | null;
}

const KIND_LABEL: Record<Kind, string> = {
  new: "Alta",
  edit: "Edición",
  remove: "Baja",
};

const KIND_COLOR: Record<Kind, string> = {
  new: "green",
  edit: "blue",
  remove: "red",
};

const KIND_ICON: Record<Kind, ReactNode> = {
  new: <UsergroupAddOutlined />,
  edit: <SolutionOutlined />,
  remove: <CloseCircleOutlined />,
};

/**
 * Admin page for validating contact-change proposals filed by
 * auditors and coordinators.
 *
 * Each row is a pending `Ticket` under the "Contactos" category. The
 * page parses the proposal JSON server-side, joins it with the
 * current contact state for an inline diff, and provides Apply /
 * Reject actions that mutate the contact graph and close the ticket
 * atomically.
 *
 * `?ticket={id}` opens the corresponding proposal modal automatically
 * — that's the target of the deep-link from the ticket detail page.
 */
// Detecta los detail del backend que indican que el contacto / producer
// referenciado en la propuesta ya no existe en BD (típico de propuestas
// "obsoletas" porque alguien editó / borró el ProducerContact desde
// otra vía mientras la propuesta seguía abierta). Cubre los tres
// mensajes que devuelve `contact_proposal_admin_viewset.apply`:
//   "Contacto a editar no encontrado"
//   "Contacto a dar de baja no encontrado"
//   "Productor no encontrado"
const isStaleReferenceError = (detail: string): boolean => {
  const t = detail.toLowerCase();
  return (
    t.includes("contacto a editar no encontrado") ||
    t.includes("contacto a dar de baja no encontrado") ||
    t.includes("productor no encontrado")
  );
};

export const ContactProposalsList = () => {
  const { t } = useTranslation();
  const { message: notify, modal } = AntdApp.useApp();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<ProposalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [positions, setPositions] = useState<BusinessPosition[]>([]);

  // Apply modal
  const [applyTarget, setApplyTarget] = useState<ProposalRow | null>(null);
  const [applyForm] = Form.useForm<ApplyFormValues>();
  const [applying, setApplying] = useState(false);

  // Reject modal
  const [rejectTarget, setRejectTarget] = useState<ProposalRow | null>(null);
  const [rejectForm] = Form.useForm<{ reason: string }>();
  const [rejecting, setRejecting] = useState(false);

  usePageTitle(t("contact_proposals.title", "Validación de contactos"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<ProposalRow[]>(
        `/admin/contact-proposals/?status=${showAll ? "all" : "open"}`,
      );
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch {
      notify.error(
        t(
          "contact_proposals.load_error",
          "Error al cargar las propuestas de contactos",
        ),
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [notify, showAll, t]);

  // Load BusinessPosition catalogue once — feeds the cargo Select in
  // the apply modal. CreatableSelect calls businessPositionService
  // when the admin creates a new one, so we don't need to refetch.
  const loadPositions = useCallback(async () => {
    try {
      const r = await apiClient.get<BusinessPosition[] | { results: BusinessPosition[] }>(
        "/business-positions/",
      );
      const list = Array.isArray(r.data)
        ? r.data
        : ((r.data as any).results ?? []);
      setPositions(list);
    } catch {
      setPositions([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadPositions();
  }, [loadPositions]);

  // Deep-link: when arriving with ?ticket={id}, auto-open the apply
  // modal on the matching row. Only fires once per id to avoid
  // re-opening after the user closes the modal.
  const autoOpenedRef = useRef<number | null>(null);
  useEffect(() => {
    const ticketParam = searchParams.get("ticket");
    if (!ticketParam) return;
    const id = Number(ticketParam);
    if (!id || autoOpenedRef.current === id) return;
    const match = rows.find((r) => r.ticket_id === id);
    if (match && match.status === 1) {
      autoOpenedRef.current = id;
      openApply(match);
    }
  }, [rows, searchParams]);

  // ----- apply --------------------------------------------------------

  /** Normalize a position name so "Representante", "REPRESENTANTE" and
   *  "  representante  " all match — accents are also stripped so a
   *  proposal that wrote "Comprador" still matches a catalogue entry
   *  saved as "Comprador" / "COMPRADOR". */
  const normPositionName = (s?: string | null) =>
    (s ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const findPositionIdByName = useCallback(
    (name?: string | null) => {
      const n = normPositionName(name);
      if (!n) return undefined;
      return positions.find((p) => normPositionName(p.name) === n)?.id;
    },
    [positions],
  );

  const openApply = (row: ProposalRow) => {
    setApplyTarget(row);
    applyForm.resetFields();
    const proposed = row.proposed || {};
    // Pre-fill `position_id` by mapping the proposed string against
    // the catalogue. If positions haven't loaded yet (race on first
    // click), the dedicated effect below re-runs the lookup once they
    // arrive so the cargo doesn't end up empty.
    const preselectedPositionId =
      findPositionIdByName(proposed.position) ??
      row.current_contact?.position_id ??
      undefined;
    applyForm.setFieldsValue({
      name: proposed.name ?? "",
      phone: proposed.phone ?? "",
      email: proposed.email ?? "",
      position_id: preselectedPositionId,
      notes: "",
    });
  };

  // Re-fill `position_id` when the BusinessPosition catalogue finishes
  // loading or the active proposal changes. Without this, opening the
  // apply modal *before* `loadPositions` returns leaves the cargo
  // field empty even if the proposed text matches a catalogue entry —
  // because `findPositionIdByName` ran against an empty array.
  useEffect(() => {
    if (!applyTarget || positions.length === 0) return;
    const current = applyForm.getFieldValue("position_id");
    if (current) return; // admin already picked / it's already set
    const fromProposal = findPositionIdByName(applyTarget.proposed?.position);
    const fallback = applyTarget.current_contact?.position_id ?? undefined;
    const next = fromProposal ?? fallback;
    if (next) {
      applyForm.setFieldsValue({ position_id: next });
    }
  }, [applyTarget, positions, applyForm, findPositionIdByName]);

  const submitApply = async () => {
    if (!applyTarget) return;
    const values = await applyForm.validateFields().catch(() => null);
    if (!values) return;
    setApplying(true);
    try {
      // Only send overrides that differ from the original proposal,
      // so the backend audit trail records "applied as proposed"
      // when the admin didn't touch anything.
      const orig = applyTarget.proposed || {};
      const overrides: Record<string, any> = {};
      (["name", "phone", "email"] as const).forEach((k) => {
        const v = (values as any)[k] ?? "";
        const o = (orig as any)[k] ?? "";
        if (v !== o) overrides[k] = v;
      });
      // Position is now a numeric id chosen from the catalogue.
      // We send it whenever it differs from the position the proposal
      // would have resolved to via its free-text hint, OR whenever the
      // admin explicitly picked one.
      const proposedPositionId = findPositionIdByName(orig.position);
      if (values.position_id !== proposedPositionId) {
        overrides.position_id = values.position_id ?? null;
      }

      const res = await apiClient.post<{
        applied?: boolean;
        kind?: string;
        seeded_chains?: {
          license?: {
            created: boolean;
            priority: number | null;
            already: boolean;
          };
          farm?: {
            farm_id: number;
            farm_name: string;
            created: boolean;
            already: boolean;
            priority: number | null;
          } | null;
        };
      }>(
        `/admin/contact-proposals/${applyTarget.ticket_id}/apply/`,
        {
          overrides,
          notes: values.notes || "",
        },
      );
      // Surface chain placement so the admin knows where the new
      // contact landed in the call queues.
      const seeded = res.data?.seeded_chains;
      if (
        seeded &&
        (seeded.license?.created || seeded.farm?.created)
      ) {
        const parts: string[] = [];
        if (seeded.license?.created) {
          parts.push(
            t(
              "contact_proposals.placed_license",
              "Licencia (pos. {{p}})",
              { p: seeded.license.priority ?? "—" },
            ),
          );
        }
        if (seeded.farm?.created) {
          parts.push(
            t(
              "contact_proposals.placed_farm",
              "{{farm}} (pos. {{p}})",
              {
                farm: seeded.farm.farm_name,
                p: seeded.farm.priority ?? "—",
              },
            ),
          );
        }
        notify.success(
          t(
            "contact_proposals.apply_success_with_chains",
            "Propuesta aplicada · añadido a {{chains}}",
            { chains: parts.join(" + ") },
          ),
        );
      } else {
        notify.success(
          t("contact_proposals.apply_success", "Propuesta aplicada"),
        );
      }
      setApplyTarget(null);
      // Clear the deep-link param once handled.
      if (searchParams.get("ticket")) {
        searchParams.delete("ticket");
        setSearchParams(searchParams, { replace: true });
      }
      await load();
    } catch (e: any) {
      // El interceptor de apiClient envuelve los 4xx en
      // { message, status, originalError } y elimina `response.data`,
      // así que el detail del backend acaba en `e.message`. Aceptamos
      // ambos caminos por si en algún momento se desactiva.
      const detail =
        e?.response?.data?.detail ||
        e?.originalError?.response?.data?.detail ||
        e?.message ||
        t("contact_proposals.apply_error", "No se pudo aplicar");

      // Cuando el detail indica que el contacto / productor
      // referenciado ya no existe (típico de propuestas obsoletas:
      // alguien editó o borró el ProducerContact desde la página del
      // productor mientras la propuesta seguía abierta), no tiene
      // sentido pedirle al admin que cambie nada del formulario — la
      // propuesta simplemente ya no se puede aplicar. Le ofrecemos
      // cerrarla como obsoleta con un solo click.
      if (applyTarget && isStaleReferenceError(detail)) {
        modal.confirm({
          title: t(
            "contact_proposals.stale_title",
            "Propuesta obsoleta",
          ),
          icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
          width: 520,
          okText: t(
            "contact_proposals.stale_close_action",
            "Cerrar como obsoleta",
          ),
          cancelText: t("common.cancel", "Cancelar"),
          okButtonProps: { danger: true },
          content: (
            <div>
              <Paragraph>
                {t(
                  "contact_proposals.stale_body",
                  "No se puede aplicar la propuesta porque el contacto o el productor referenciado ya no existe en la base de datos. Lo más probable es que se editara o eliminara directamente desde la pestaña del productor mientras la propuesta estaba abierta.",
                )}
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                <Text type="secondary">
                  {t(
                    "contact_proposals.stale_detail_hint",
                    "Error del backend",
                  )}
                  : <Text code>{detail}</Text>
                </Text>
              </Paragraph>
            </div>
          ),
          onOk: async () => {
            const target = applyTarget;
            try {
              await apiClient.post(
                `/admin/contact-proposals/${target.ticket_id}/reject/`,
                {
                  reason: t(
                    "contact_proposals.stale_auto_reason",
                    "Cierre automático: el contacto/productor referenciado ya no existe en BD. Probablemente fue modificado fuera del flujo de propuestas.",
                  ),
                },
              );
              notify.success(
                t(
                  "contact_proposals.stale_closed",
                  "Propuesta cerrada como obsoleta",
                ),
              );
              setApplyTarget(null);
              if (searchParams.get("ticket")) {
                searchParams.delete("ticket");
                setSearchParams(searchParams, { replace: true });
              }
              await load();
            } catch (rejErr: any) {
              const rejDetail =
                rejErr?.response?.data?.detail ||
                rejErr?.originalError?.response?.data?.detail ||
                rejErr?.message ||
                t(
                  "contact_proposals.reject_error",
                  "No se pudo rechazar",
                );
              notify.error(rejDetail);
            }
          },
        });
        return;
      }

      notify.error(detail);
    } finally {
      setApplying(false);
    }
  };

  // ----- reject -------------------------------------------------------

  const openReject = (row: ProposalRow) => {
    setRejectTarget(row);
    rejectForm.resetFields();
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    const values = await rejectForm.validateFields().catch(() => null);
    if (!values) return;
    setRejecting(true);
    try {
      await apiClient.post(
        `/admin/contact-proposals/${rejectTarget.ticket_id}/reject/`,
        { reason: values.reason },
      );
      notify.success(
        t("contact_proposals.reject_success", "Propuesta rechazada"),
      );
      setRejectTarget(null);
      if (searchParams.get("ticket")) {
        searchParams.delete("ticket");
        setSearchParams(searchParams, { replace: true });
      }
      await load();
    } catch (e: any) {
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.reason ||
        e?.message ||
        t("contact_proposals.reject_error", "No se pudo rechazar");
      notify.error(detail);
    } finally {
      setRejecting(false);
    }
  };

  // ----- table --------------------------------------------------------

  const columns = useMemo(
    () => [
      {
        title: t("contact_proposals.kind", "Tipo"),
        dataIndex: "kind",
        key: "kind",
        width: 110,
        filters: [
          { value: "new", text: KIND_LABEL.new },
          { value: "edit", text: KIND_LABEL.edit },
          { value: "remove", text: KIND_LABEL.remove },
        ],
        onFilter: (value: any, row: ProposalRow) => row.kind === value,
        render: (kind: Kind) => (
          <Tag icon={KIND_ICON[kind]} color={KIND_COLOR[kind]}>
            {KIND_LABEL[kind]}
          </Tag>
        ),
      },
      {
        title: t("contact_proposals.audit", "Auditoría"),
        key: "audit",
        width: 160,
        render: (_: any, row: ProposalRow) =>
          row.audit_id ? (
            <Link to={`/audits/${row.audit_id}`} target="_blank">
              {row.audit_reference ?? `#${row.audit_id}`}{" "}
              <ExportOutlined style={{ fontSize: 11 }} />
            </Link>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: t("contact_proposals.producer", "Productor / Finca"),
        key: "producer",
        render: (_: any, row: ProposalRow) => (
          <div>
            <div>{row.producer_name ?? "—"}</div>
            {row.farm_name && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.farm_name}
              </Text>
            )}
          </div>
        ),
      },
      {
        title: t("contact_proposals.contact", "Contacto afectado"),
        key: "contact",
        render: (_: any, row: ProposalRow) => {
          if (row.kind === "new") {
            return (
              <Text type="secondary">
                {t("contact_proposals.new_contact", "Contacto nuevo")}
              </Text>
            );
          }
          return (
            <div>
              <strong>{row.contact_name ?? "—"}</strong>
              {row.contact_email && (
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  {row.contact_email}
                </div>
              )}
              {row.contact_phone && (
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                  {formatPhone(row.contact_phone)}
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: t("contact_proposals.proposed_by", "Propuesto por"),
        key: "created_by",
        width: 160,
        render: (_: any, row: ProposalRow) => (
          <div>
            <div>{row.created_by ?? "—"}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {new Date(row.created).toLocaleDateString()}
            </Text>
          </div>
        ),
      },
      {
        title: t("contact_proposals.ticket", "Ticket"),
        key: "ticket",
        width: 130,
        render: (_: any, row: ProposalRow) => (
          <Link to={`/admin/tickets/${row.ticket_id}`}>
            <Tag color="#FF8C00">{row.ticket_code}</Tag>
          </Link>
        ),
      },
      {
        title: t("common.actions", "Acciones"),
        key: "actions",
        width: 120,
        align: "right" as const,
        render: (_: any, row: ProposalRow) => {
          if (row.status !== 1) {
            return (
              <Tooltip title={row.resolution_notes ?? row.status_label}>
                <Tag
                  color={row.status === 2 ? "green" : "default"}
                  icon={row.status === 2 ? <CheckCircleOutlined /> : null}
                >
                  {row.status_label}
                </Tag>
              </Tooltip>
            );
          }
          return (
            <Space size="small">
              <Tooltip title={t("contact_proposals.apply", "Validar")}>
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => openApply(row)}
                  aria-label={t("contact_proposals.apply", "Validar")}
                />
              </Tooltip>
              <Tooltip title={t("contact_proposals.reject", "Rechazar")}>
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => openReject(row)}
                  aria-label={t("contact_proposals.reject", "Rechazar")}
                />
              </Tooltip>
            </Space>
          );
        },
      },
    ],
    [t],
  );

  const expandedRowRender = (row: ProposalRow) => (
    <ProposalDiff row={row} />
  );

  return (
    <Card>
      <PageHeader
        icon={<SolutionOutlined />}
        accent="#1677FF"
        title={t("contact_proposals.title", "Validación de contactos")}
        subtitle={t(
          "contact_proposals.subtitle",
          "Propuestas de alta, edición o baja de contactos creadas por auditores y coordinadores en sus auditorías. Aplica los cambios o rechaza la propuesta — en ambos casos el ticket asociado se cierra automáticamente.",
        )}
        extra={
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void load()}
              loading={loading}
            >
              {t("common.reload", "Refrescar")}
            </Button>
            <Space size={8} align="center">
              <Text style={{ fontSize: 13 }}>
                {t(
                  "contact_proposals.show_full_history",
                  "Ver historial completo",
                )}
              </Text>
              <Switch
                checked={showAll}
                onChange={setShowAll}
                checkedChildren={t("common.on", "ON")}
                unCheckedChildren={t("common.off", "OFF")}
              />
            </Space>
          </Space>
        }
      />

      <Alert
        showIcon
        type="info"
        style={{ marginBottom: 12 }}
        message={t(
          "contact_proposals.banner",
          "Despliega cada fila para comparar los datos actuales con los propuestos. Al validar puedes ajustar manualmente los campos antes de aplicar.",
        )}
      />

      <Table<ProposalRow>
        dataSource={rows}
        columns={columns}
        rowKey="ticket_id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 1200 }}
        expandable={{
          expandedRowRender,
          rowExpandable: () => true,
        }}
        rowClassName={(row) =>
          row.ticket_id === Number(searchParams.get("ticket"))
            ? "row-highlighted"
            : ""
        }
      />

      {/* Apply modal */}
      <ApplyModal
        target={applyTarget}
        form={applyForm}
        applying={applying}
        positions={positions}
        onPositionCreated={(p) =>
          setPositions((prev) =>
            prev.some((x) => x.id === p.id) ? prev : [...prev, p],
          )
        }
        onClose={() => setApplyTarget(null)}
        onSubmit={submitApply}
      />

      {/* Reject modal */}
      <Modal
        open={!!rejectTarget}
        title={t("contact_proposals.reject_title", "Rechazar propuesta")}
        onCancel={() => (rejecting ? undefined : setRejectTarget(null))}
        onOk={submitReject}
        confirmLoading={rejecting}
        okText={t("contact_proposals.reject", "Rechazar")}
        cancelText={t("common.cancel", "Cancelar")}
        okButtonProps={{ danger: true }}
        destroyOnHidden
      >
        {rejectTarget && (
          <>
            <Paragraph>
              {t(
                "contact_proposals.reject_hint",
                "El motivo se enviará al auditor o coordinador que propuso el cambio. La propuesta se cerrará sin aplicar ningún cambio en los contactos.",
              )}
            </Paragraph>
            <Form form={rejectForm} layout="vertical">
              <Form.Item
                label={t("contact_proposals.reason", "Motivo")}
                name="reason"
                rules={[
                  {
                    required: true,
                    message: t(
                      "contact_proposals.reason_required",
                      "Indica el motivo del rechazo",
                    ),
                  },
                  { max: 1000 },
                ]}
              >
                <Input.TextArea
                  rows={4}
                  maxLength={1000}
                  showCount
                  placeholder={t(
                    "contact_proposals.reason_placeholder",
                    "Ej: el contacto propuesto ya existe bajo otro productor; ...",
                  )}
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </Card>
  );
};

// -------- subcomponents -----------------------------------------------

const ProposalDiff: React.FC<{ row: ProposalRow }> = ({ row }) => {
  const { t } = useTranslation();
  const proposed = row.proposed || {};
  const current = row.current_contact;
  return (
    <div style={{ padding: 12 }}>
      {row.reason && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t("contact_proposals.reason", "Motivo")}
          description={row.reason}
        />
      )}
      <Space align="start" size={24} wrap>
        <Descriptions
          column={1}
          size="small"
          bordered
          title={t("contact_proposals.current", "Estado actual")}
          style={{ minWidth: 320 }}
        >
          <Descriptions.Item
            label={t("contact_proposals.name", "Nombre")}
          >
            {current?.full_name ?? row.contact_name ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("contact_proposals.email", "Email")}
          >
            {current?.email ?? row.contact_email ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("contact_proposals.phone", "Teléfono")}
          >
            {formatPhone(current?.phone ?? row.contact_phone ?? "") || "—"}
          </Descriptions.Item>
          <Descriptions.Item
            label={t("contact_proposals.position", "Cargo")}
          >
            {current?.position_name ?? "—"}
          </Descriptions.Item>
          {current && !current.is_active && (
            <Descriptions.Item label={t("common.status", "Estado")}>
              <Tag color="red">
                {t("contact_proposals.inactive", "Inactivo")}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {row.kind === "remove" ? (
          <Alert
            type="error"
            showIcon
            message={t(
              "contact_proposals.will_deactivate",
              "Al validar, este contacto se marcará como inactivo en el productor.",
            )}
            style={{ maxWidth: 320 }}
          />
        ) : (
          <Descriptions
            column={1}
            size="small"
            bordered
            title={t(
              "contact_proposals.proposed",
              row.kind === "new" ? "Nuevo contacto" : "Cambios propuestos",
            )}
            style={{ minWidth: 320 }}
          >
            <Descriptions.Item
              label={t("contact_proposals.name", "Nombre")}
            >
              <DiffValue
                current={current?.full_name}
                next={proposed.name}
                emphasizeNew={row.kind === "new"}
              />
            </Descriptions.Item>
            <Descriptions.Item
              label={t("contact_proposals.email", "Email")}
            >
              <DiffValue
                current={current?.email ?? undefined}
                next={proposed.email}
                emphasizeNew={row.kind === "new"}
              />
            </Descriptions.Item>
            <Descriptions.Item
              label={t("contact_proposals.phone", "Teléfono")}
            >
              <DiffValue
                current={current?.phone ?? undefined}
                next={proposed.phone}
                emphasizeNew={row.kind === "new"}
              />
            </Descriptions.Item>
            <Descriptions.Item
              label={t("contact_proposals.position", "Cargo")}
            >
              <DiffValue
                current={current?.position_name ?? undefined}
                next={proposed.position}
                emphasizeNew={row.kind === "new"}
              />
            </Descriptions.Item>
          </Descriptions>
        )}
      </Space>
    </div>
  );
};

const DiffValue: React.FC<{
  current?: string;
  next?: string;
  emphasizeNew?: boolean;
}> = ({ current, next, emphasizeNew }) => {
  const cur = (current || "").trim();
  const nxt = (next || "").trim();
  if (!nxt) return <Text type="secondary">—</Text>;
  if (emphasizeNew || !cur) return <strong>{nxt}</strong>;
  if (cur === nxt) return <span>{nxt}</span>;
  return (
    <span>
      <Text delete type="secondary" style={{ marginRight: 6 }}>
        {cur || "—"}
      </Text>
      <strong style={{ color: "#1677FF" }}>{nxt}</strong>
    </span>
  );
};

const ApplyModal: React.FC<{
  target: ProposalRow | null;
  form: any;
  applying: boolean;
  positions: BusinessPosition[];
  onPositionCreated: (p: BusinessPosition) => void;
  onClose: () => void;
  onSubmit: () => void;
}> = ({ target, form, applying, positions, onPositionCreated, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const positionOptions = useMemo(
    () => positions.map((p) => ({ value: p.id, label: p.name })),
    [positions],
  );
  return (
    <Modal
      open={!!target}
      title={
        target
          ? t(
              "contact_proposals.apply_title",
              "Validar propuesta — {{kind}}",
              { kind: KIND_LABEL[target.kind] },
            )
          : ""
      }
      onCancel={() => (applying ? undefined : onClose())}
      onOk={onSubmit}
      confirmLoading={applying}
      okText={t("contact_proposals.apply_confirm", "Aplicar")}
      cancelText={t("common.cancel", "Cancelar")}
      destroyOnHidden
      width={620}
    >
      {target && (
        <>
          {target.kind === "remove" ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(
                "contact_proposals.confirm_remove",
                "Vas a marcar como inactivo a {{name}} en {{producer}}. Las auditorías históricas conservarán la referencia.",
                {
                  name: target.contact_name ?? "—",
                  producer: target.producer_name ?? "—",
                },
              )}
            />
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(
                "contact_proposals.apply_hint",
                "Revisa los datos antes de aplicar. Puedes corregir cualquier campo aquí — si lo modificas, el cambio sobreescribe lo propuesto.",
              )}
            />
          )}

          {target.kind !== "remove" && (
            <Form form={form} layout="vertical">
              <Form.Item
                label={t("contact_proposals.name", "Nombre completo")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("contact_proposals.name_required", "Obligatorio"),
                  },
                  { max: 400 },
                ]}
              >
                <Input
                  placeholder={t(
                    "contact_proposals.name_placeholder",
                    "Nombre y apellidos",
                  )}
                />
              </Form.Item>
              <Form.Item
                label={t("contact_proposals.email", "Email")}
                name="email"
                rules={[
                  {
                    type: "email",
                    message: t("contact_proposals.email_invalid", "Email no válido"),
                  },
                ]}
              >
                <Input placeholder="email@example.com" />
              </Form.Item>
              <Form.Item
                label={t("contact_proposals.phone", "Teléfono")}
                name="phone"
                extra={t(
                  "contact_proposals.phone_hint",
                  "Selecciona el prefijo internacional y escribe el número. La propuesta del auditor se guarda con prefijo incluido.",
                )}
              >
                <PhoneInput
                  placeholder={t(
                    "contact_proposals.phone_placeholder",
                    "Número",
                  )}
                  popupContainer={() => document.body}
                />
              </Form.Item>
              <Form.Item
                label={t("contact_proposals.position", "Cargo")}
                name="position_id"
                extra={t(
                  "contact_proposals.position_hint_v2",
                  "Elige un cargo del catálogo o crea uno nuevo desde el desplegable.",
                )}
              >
                <CreatableSelect
                  options={positionOptions}
                  placeholder={t(
                    "contact_proposals.position_placeholder",
                    "Selecciona o crea un cargo",
                  )}
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  // Mount the popup at document.body and lift its z-index
                  // above the Modal's so it's never clipped by the modal body.
                  getPopupContainer={() => document.body}
                  dropdownStyle={{ zIndex: 10000 }}
                  onCreateNew={async (name) => {
                    const created =
                      await businessPositionService.createPosition(name);
                    onPositionCreated({
                      id: created.id,
                      name: created.name,
                    });
                    return created;
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t("contact_proposals.notes", "Notas internas (opcional)")}
                name="notes"
                rules={[{ max: 500 }]}
              >
                <Input.TextArea rows={2} placeholder={t("contact_proposals.notes_placeholder", "Visible en el historial del ticket")} />
              </Form.Item>
            </Form>
          )}
        </>
      )}
    </Modal>
  );
};

export default ContactProposalsList;
